import { useState, useEffect } from "react"
import { usePagination } from "../../Pagination/usePagination"
import { CustomSortIterateeOutput, TableColumnSortOrder } from "../types"
import { getNewDataPage, getSortedData } from "../utils"

export const usePaginatedTable = <T extends {}, F extends unknown>(
  initalData: ReadonlyArray<T>,
  options?: {
    searchFields?: Array<keyof T>
    onFilter?: (data: ReadonlyArray<T>, filterTerm: F | null) => ReadonlyArray<T>
    doNotResetPaginationWhenDataChanges?: boolean
  }
) => {
  const [data, setData] = useState<ReadonlyArray<T>>([])
  const [sortedData, setSortedData] = useState<ReadonlyArray<T>>([])
  const [dataCount, setDataCount] = useState(0)
  const [page, setPageState] = useState<ReadonlyArray<T>>([])
  const [sort, setSort] = useState<{ property: keyof T; sort: TableColumnSortOrder | undefined } | undefined>(undefined)
  const [filter, setFilter] = useState<F | null>(null)
  const [search, setSearch] = useState<string>(``)

  const setPage = (newPage: ReadonlyArray<T>) => {
    setPageState(newPage)
  }

  const paginationHandler = (pageIndex: number, pageSize: number) => {
    setPage(getNewDataPage(sortedData, pageIndex, pageSize))
  }
  const pagination = usePagination(dataCount, paginationHandler)

  useEffect(() => {
    setData(initalData)
    setSortedData(initalData)
    setDataCount(initalData.length)
    if (!options?.doNotResetPaginationWhenDataChanges) {
      setPage(initalData.slice(0, 10))
    } else {
      setPage(getNewDataPage(initalData, pagination.pageIndex, pagination.pageSize))
    }
  }, [initalData])

  const sortData = (
    property: keyof T,
    newSort: TableColumnSortOrder | undefined,
    sortFn?: (item: T) => CustomSortIterateeOutput
  ) => {
    const newSortedData = getSortedData(data, property, newSort, sortFn)
    setSort({ property, sort: newSort })
    setSortedData(newSortedData)
    setPage(getNewDataPage(newSortedData, 0, pagination.pageSize))
  }

  const searchData = (term: string) => {
    if (!term || !options?.searchFields) {
      setSearch(``)
      setDataCount(sortedData.length)
      setPage(getNewDataPage(sortedData, 0, pagination.pageSize))
      return
    }

    const searchedData = sortedData.filter((it) =>
      (options.searchFields || [])
        .map((key) => it[key])
        .some((field) =>
          ((field as unknown as string) || ``)
            .toLowerCase()
            .replace(/\s+/g, ``)
            .includes(term.toLowerCase().replace(/\s+/g, ``))
        )
    )

    setSearch(term)
    setDataCount(searchedData.length)
    setPage(getNewDataPage(searchedData, 0, pagination.pageSize))
  }

  const filterData = (term: F | null) => {
    if (!options?.onFilter) return
    const filteredData = options.onFilter(sortedData, term)
    setFilter(term)
    setDataCount(filteredData.length)
    setPage(getNewDataPage(filteredData, 0, pagination.pageSize))
  }

  return {
    page,
    sort,
    filter,
    search,
    dataCount,
    pagination,
    sortData,
    searchData,
    filterData,
  }
}
